import {
  formatPlanningStartDateFormat,
  hasPlanningMileageFormat,
  hasPlanningStartDateFormat,
} from "@qivia/ui/src/format";
import type { ImportError } from "@qivia/ui";
import * as XLSX from "xlsx";
import type { ContractDataVehicle } from "./vehicleContractDataApi";

export const CONTRACT_DATA_COLUMNS = [
  "Immatriculation",
  "Référence",
  "Date début contrat (DD/MM/YYYY)",
  "Date fin contrat (DD/MM/YYYY)",
  "Kilométrage contractuel (km)",
  "Coût par km supplémentaire (€)",
] as const;

const getExcelColumn = (index: number): string => {
  let n = index + 1;
  let col = "";
  while (n > 0) {
    const r = (n - 1) % 26;
    col = String.fromCharCode(65 + r) + col;
    n = Math.floor((n - 1) / 26);
  }
  return col;
};

const parseDateToISO = (dateStr: string): string => {
  const parts = formatPlanningStartDateFormat(dateStr);
  if (!parts) throw new Error(`Invalid date: ${dateStr}`);
  const [day, month, year] = parts;
  return new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day)),
  ).toISOString();
};

export const getContractDataHeaderErrors = (
  headers: unknown[],
): ImportError[] =>
  CONTRACT_DATA_COLUMNS.flatMap((colName) =>
    headers.includes(colName)
      ? []
      : [{ cell: "", content: colName, errorType: "missingHeader" }],
  );

export const getContractDataLineErrors = (rows: unknown[][]): ImportError[] => {
  const headers = (rows[0] ?? []) as string[];
  const col = (name: string) => headers.indexOf(name);

  return rows.slice(1).flatMap((row, rowIndex) => {
    const isEmpty = row.every(
      (v) => v === undefined || v === null || String(v).trim() === "",
    );
    if (isEmpty) return [];

    const excelRow = rowIndex + 2;
    const cell = (colName: string) =>
      `${getExcelColumn(col(colName))}${excelRow}`;
    const errors: ImportError[] = [];

    const regNum = row[col("Immatriculation")];
    const ref = row[col("Référence")];
    if (!regNum && !ref) {
      errors.push({
        cell: cell("Immatriculation"),
        content: "",
        errorType: "missingIdentifier",
      });
    }

    const startDate = row[col("Date début contrat (DD/MM/YYYY)")];
    if (!startDate) {
      errors.push({
        cell: cell("Date début contrat (DD/MM/YYYY)"),
        content: "",
        errorType: "missingValue",
      });
    } else if (!hasPlanningStartDateFormat(String(startDate))) {
      errors.push({
        cell: cell("Date début contrat (DD/MM/YYYY)"),
        content: String(startDate),
        errorType: "dateFormat",
      });
    }

    const endDate = row[col("Date fin contrat (DD/MM/YYYY)")];
    if (!endDate) {
      errors.push({
        cell: cell("Date fin contrat (DD/MM/YYYY)"),
        content: "",
        errorType: "missingValue",
      });
    } else if (!hasPlanningStartDateFormat(String(endDate))) {
      errors.push({
        cell: cell("Date fin contrat (DD/MM/YYYY)"),
        content: String(endDate),
        errorType: "dateFormat",
      });
    }

    const mileage = row[col("Kilométrage contractuel (km)")];
    if (!mileage) {
      errors.push({
        cell: cell("Kilométrage contractuel (km)"),
        content: "",
        errorType: "missingValue",
      });
    } else if (!hasPlanningMileageFormat(String(mileage))) {
      errors.push({
        cell: cell("Kilométrage contractuel (km)"),
        content: String(mileage),
        errorType: "numberFormat",
      });
    }

    const cost = row[col("Coût par km supplémentaire (€)")];
    if (!cost) {
      errors.push({
        cell: cell("Coût par km supplémentaire (€)"),
        content: "",
        errorType: "missingValue",
      });
    } else if (!hasPlanningMileageFormat(String(cost))) {
      errors.push({
        cell: cell("Coût par km supplémentaire (€)"),
        content: String(cost),
        errorType: "numberFormat",
      });
    }

    return errors;
  });
};

export const parseContractDataFile = (
  file: File,
): Promise<ContractDataVehicle[]> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);
    fileReader.onload = (event) => {
      if (!event?.target) return reject(new Error("Failed to read file"));
      try {
        const workBook = XLSX.read(event.target.result, { type: "buffer" });
        const sheet = workBook.Sheets[workBook.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, {
          header: 1,
          raw: false,
        });

        const headers = rows[0] as string[];
        const col = (name: string) => headers.indexOf(name);

        const vehicles = rows
          .slice(1)
          .filter((row) =>
            row.some(
              (v) => v !== undefined && v !== null && String(v).trim() !== "",
            ),
          )
          .map((row) => {
            const regNum = row[col("Immatriculation")];
            const ref = row[col("Référence")];
            const startDate = String(
              row[col("Date début contrat (DD/MM/YYYY)")],
            );
            const endDate = String(row[col("Date fin contrat (DD/MM/YYYY)")]);
            const mileage = String(
              row[col("Kilométrage contractuel (km)")],
            ).replace(",", ".");
            const cost = String(
              row[col("Coût par km supplémentaire (€)")],
            ).replace(",", ".");

            return {
              registrationNumber: regNum
                ? String(regNum).trim().toUpperCase()
                : null,
              reference: ref ? String(ref).trim().toUpperCase() : null,
              contractStartDate: parseDateToISO(startDate),
              contractEndDate: parseDateToISO(endDate),
              contractMileage: Number(mileage),
              additionalCostPerKm: Math.round(Number(cost) * 100),
            };
          });

        resolve(vehicles);
      } catch (e) {
        reject(e);
      }
    };
  });
